import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { adminApi } from '../../lib/api';
import { 
  BarChart3, Users, CheckSquare, TrendingUp, Crown, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

const AdminReports = () => {
  const [period, setPeriod] = useState('30d');
  const [userReports, setUserReports] = useState(null);
  const [taskReports, setTaskReports] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const [userRes, taskRes] = await Promise.all([
        adminApi.getUserReports(period),
        adminApi.getTaskReports()
      ]);
      setUserReports(userRes.data);
      setTaskReports(taskRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 text-[#4A9CC7] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const maxSignups = Math.max(...(userReports?.signups_by_day?.map(d => d.count) || [1]));

  return (
    <AdminLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="admin-reports-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope' }}>
              Reports & Analytics
            </h1>
            <p className="text-gray-600 dark:text-gray-400">Track user growth and app usage</p>
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[180px] rounded-xl" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="14d">Last 14 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="60d">Last 60 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* User Signups Chart */}
          <Card data-testid="signups-chart">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#4A9CC7]" />
                User Signups
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {userReports?.signups_by_day?.slice(-10).map((day, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-sm text-gray-500 dark:text-gray-400 w-20">{day.date.slice(5)}</span>
                    <div className="flex-1 h-6 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#4A9CC7] rounded-full transition-all"
                        style={{ width: `${(day.count / maxSignups) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium w-8 text-right">{day.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Tier Distribution */}
          <Card data-testid="tier-distribution">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-500" />
                User Tiers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {userReports?.tier_distribution?.map((tier, i) => {
                  const total = userReports.tier_distribution.reduce((a, b) => a + b.count, 0);
                  const percentage = total > 0 ? Math.round((tier.count / total) * 100) : 0;
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium capitalize flex items-center gap-2">
                          {tier.tier === 'premium' && <Crown className="w-4 h-4 text-yellow-500" />}
                          {tier.tier}
                        </span>
                        <span className="text-gray-600 dark:text-gray-400">{tier.count} users ({percentage}%)</span>
                      </div>
                      <div className="h-3 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${tier.tier === 'premium' ? 'bg-yellow-500' : 'bg-gray-400'}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-500/10 rounded-xl">
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  <strong>Premium Conversion:</strong> {
                    userReports?.tier_distribution ? 
                    Math.round((userReports.tier_distribution.find(t => t.tier === 'premium')?.count || 0) / 
                    userReports.tier_distribution.reduce((a, b) => a + b.count, 0) * 100) : 0
                  }% of users
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Task Priority Distribution */}
          <Card data-testid="priority-distribution">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckSquare className="w-5 h-5 text-green-500" />
                Tasks by Priority
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {taskReports?.by_priority?.map((item, i) => {
                  const total = taskReports.by_priority.reduce((a, b) => a + b.count, 0);
                  const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
                  const colors = {
                    high: 'bg-red-500',
                    medium: 'bg-yellow-500',
                    low: 'bg-green-500'
                  };
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium capitalize">{item.priority}</span>
                        <span className="text-gray-600 dark:text-gray-400">{item.count} ({percentage}%)</span>
                      </div>
                      <div className="h-3 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${colors[item.priority]}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Task Label Distribution */}
          <Card data-testid="label-distribution">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-500" />
                Tasks by Label
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {taskReports?.by_label?.map((item, i) => {
                  const maxCount = Math.max(...taskReports.by_label.map(l => l.count));
                  const percentage = maxCount > 0 ? Math.round((item.count / maxCount) * 100) : 0;
                  const colors = {
                    Work: 'bg-blue-500',
                    Personal: 'bg-purple-500',
                    Study: 'bg-orange-500',
                    Health: 'bg-green-500'
                  };
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{item.label}</span>
                        <span className="text-gray-600 dark:text-gray-400">{item.count}</span>
                      </div>
                      <div className="h-3 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${colors[item.label] || 'bg-gray-400'}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Completion Rate */}
        <Card data-testid="completion-rate">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#4A9CC7]" />
              Daily Completion Rate (Last 7 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2">
              {taskReports?.completion_by_day?.map((day, i) => (
                <div key={i} className="text-center">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{day.date.slice(5)}</div>
                  <div className="h-24 bg-gray-100 dark:bg-slate-700 rounded-lg relative overflow-hidden">
                    <div 
                      className="absolute bottom-0 left-0 right-0 bg-[#4A9CC7] transition-all"
                      style={{ height: `${day.rate}%` }}
                    />
                  </div>
                  <div className="text-sm font-medium mt-2">{day.rate}%</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{day.completed}/{day.total}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Active Users */}
        <Card className="mt-6" data-testid="top-users">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#4A9CC7]" />
              Top Active Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {userReports?.top_active_users?.map((user, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-800 rounded-xl">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#4A9CC7] text-white text-sm flex items-center justify-center font-medium">
                      {i + 1}
                    </span>
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-[#4A9CC7]">{user.task_count} tasks</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user.tier} tier</p>
                  </div>
                </div>
              ))}
              {(!userReports?.top_active_users || userReports.top_active_users.length === 0) && (
                <p className="text-center text-gray-500 py-4">No active users yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminReports;
